import { addMonths, endOfYear, format, startOfYear } from "date-fns";

const DEFAULT_DATE_FORMAT = "yyyy-MM-dd";
/**
 * Returns the start and end date of the calendar year for the given date or year.
 *
 * @param dateOrYear - The date or year number for which to get the calendar year range.
 * @returns An object containing:
 *   - `startDate`: The first day of the year in 'yyyy-MM-dd' format.
 *   - `endDate`: The last day of the year in 'yyyy-MM-dd' format.
 *
 * @example
 * ```typescript
 * getCalendarYearRange(new Date('2025-07-25'));
 * // => { startDate: '2025-01-01', endDate: '2025-12-31' }
 * getCalendarYearRange(2024);
 * // => { startDate: '2024-01-01', endDate: '2024-12-31' }
 * ```
 */
export const getCalendarYearRange = (dateOrYear: Date | number) => {
  const date =
    typeof dateOrYear === "number" ? new Date(dateOrYear, 0, 1) : dateOrYear;
  const startDate = startOfYear(date);
  const endDate = endOfYear(date);
  return {
    startDate: format(startDate, DEFAULT_DATE_FORMAT),
    endDate: format(endDate, DEFAULT_DATE_FORMAT),
  };
};

/**
 * Rounds a Date to the nearest 30-minute mark and returns an "HH:MM" string.
 *
 * Buckets: minutes [0..14] → :00, [15..44] → :30, [45..59] → next hour :00.
 */
export const roundToNearest30m = (date: Date): string => {
  const d = new Date(date);
  d.setSeconds(0, 0);
  const minutes = d.getMinutes();
  if (minutes < 15) {
    d.setMinutes(0);
  } else if (minutes < 45) {
    d.setMinutes(30);
  } else {
    d.setHours(d.getHours() + 1);
    d.setMinutes(0);
  }
  return `${d.getHours().toString().padStart(2, "0")}:${d.getMinutes().toString().padStart(2, "0")}`;
};

export const isSameDay = (a: Date, b: Date): boolean =>
  a.getFullYear() === b.getFullYear() &&
  a.getMonth() === b.getMonth() &&
  a.getDate() === b.getDate();

/**
 * Formats a Date / ISO string / epoch-ms as "HH:MM" (24-hour, local time).
 */
export const formatHHMM = (input: Date | number | string): string => {
  const d = new Date(input);
  return `${d.getHours().toString().padStart(2, "0")}:${d.getMinutes().toString().padStart(2, "0")}`;
};

/**
 * Converts "HH:MM" (24-hour) time string to "h:MM AM/PM" (12-hour) format.
 */
export const formatTo12Hour = (timeString: string): string => {
  const [hours, minutes] = timeString.split(":").map(Number);
  const period = hours >= 12 ? "PM" : "AM";
  const displayHours = hours % 12 || 12;
  return `${displayHours}:${minutes.toString().padStart(2, "0")} ${period}`;
};

/**
 * Combines a calendar date and "HH:MM" time string into a unix timestamp (seconds).
 * The date's year/month/day are used; hours/minutes come from the time string.
 */
export const parseTimeToUnixSeconds = (
  date: Date,
  timeString: string,
): number => {
  const [hours, minutes] = timeString.split(":").map(Number);
  const d = new Date(date);
  d.setHours(hours, minutes, 0, 0);
  return Math.floor(d.getTime() / 1000);
};

/**
 * Formats a Date as the API's `year_month` field, e.g. "202504".
 * Month is 1-indexed (Jan = "01", Dec = "12") to match the backend's `year_month` contract.
 */
export const formatYearMonth = (date: Date): string =>
  `${date.getFullYear()}${String(date.getMonth() + 1).padStart(2, "0")}`;

/**
 * Build month-named options for a "Timeframe" select. Index 0 is the current
 * month and is labelled "{Name} (This month)"; subsequent indices are the next
 * months by name. `count` includes the current month.
 */
export const getMonthOptions = (
  count: number,
): { value: string; label: string }[] =>
  Array.from({ length: count }, (_, i) => {
    const date = addMonths(new Date(), i);
    const name = format(date, "MMMM");
    return {
      value: String(i),
      label: i === 0 ? `${name} (This month)` : name,
    };
  });
